import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Modal } from "antd";
import StepIndicator from "./indicator";
import Button from "../button";
import Icon from "../../assets/icons";
import { showToast } from "../toast";
import { RingLoader } from "react-spinners";
import useApi from "../hooks/request";
import RequisitionInformation from "./requisition-info";
import useRequest from "../hooks/use-request";
import { I_ProductData } from "../../pages/inventory/types";
import FIlterSelect from "../filter-select";
import Input from "../input";
import { Plus, Trash2 } from "lucide-react";

interface AddRequisitionDialogProps {
    isVisible: boolean;
    onClose: () => void;
    requisition?: any;
}

interface RequisitionItem {
    productId: string;
    productName: string;
    quantity: number;
}

type FormStep = "requisitionInfo" | "products";
const AddRequisitionDialog: React.FC<AddRequisitionDialogProps> = ({
    isVisible,
    onClose,
    requisition,
}) => {
    const [currentStep, setCurrentStep] = useState<FormStep>("requisitionInfo");
    const [product, setProduct] = useState<I_ProductData[]>([]);
    const [requisitionItems, setRequisitionItems] = useState<RequisitionItem[]>([
        { productId: "", productName: "", quantity: 0 }
    ]);
    const userToken = localStorage.getItem("token");

    const {
        control,
        setValue,
        handleSubmit,
        trigger,
        clearErrors,
        watch,
        formState: { errors },
    } = useForm({
        mode: "onChange",
    });

    const {loading, makeRequest} = useApi("/purchase-requisition", "POST");

    const { makeRequest: getProducts } = useRequest("/inventory", "GET", {
          Authorization: `Bearer ${userToken}`,
    });

    const productOptions =
    product?.map((item: any) => ({
      value: item._id,
      label: item.name,
    })) || [];

    useEffect(() => {
        const fetchProducts = async () => {
            const [response] = await getProducts();
            if (response.status) {
            setProduct(response.data?.inventory?.docs);
            }
        };
        fetchProducts();
    }, []);

    const handleCreateRequisition = handleSubmit(async(formData) => {
        // Filter out empty items
        const validItems = requisitionItems.filter(item => 
            item.productId && item.quantity > 0
        );

        if (validItems.length === 0) {
            showToast("At least one product item is required", false, { position: "top-right" });
            return;
        }

        const requisition = {
            regionName: formData.regionName,
            sourcingOfficers: (formData.sourcingOfficers || []).map((officerId: string) => ({
                _id: officerId,
            })),
            
            dateRequired: formData.dateRequired,
            urgencyLevel: formData.urgencyLevel,
            Item: validItems.map(item => ({
                _id: item.productId,
                quantity: item.quantity
            }))
        };

        // Uncomment to enable API call
        const [response] = await makeRequest(requisition);
        if (response.status) {
            showToast(response.message, true, { position: "top-right" });
            onClose();
            window.location.reload();
        } else {
            showToast(response?.message, false, { position: "top-right" });
        }
    });

    const validateAndProceed = async () => {
        let isValid = true;

        if(currentStep === "requisitionInfo") {
            const fieldsToValidate = [
                "regionName",
                "sourcingOfficers",
                "urgencyLevel",
                "dateRequired"
            ];
            isValid = await trigger(fieldsToValidate);
        } else if (currentStep === "products") {
            // Product validation is handled in the submit function
            isValid = true;
        }

        if(isValid){
            if(currentStep === "requisitionInfo") {
                setCurrentStep("products")
            } else {
                handleCreateRequisition();
            }
        }
    };

    const goBackToPreviousStep = () => {
        if (currentStep === "products") {
            setCurrentStep("requisitionInfo");
        }
    };

    const addProduct = () => {
        setRequisitionItems([
            ...requisitionItems,
            { productId: "", productName: "", quantity: 0 }
        ]);
    };

    const removeProduct = (index: number) => {
        if (requisitionItems.length > 1) {
            const updatedItems = [...requisitionItems];
            updatedItems.splice(index, 1);
            setRequisitionItems(updatedItems);
        } else {
            showToast("At least one product is required", false, { position: "top-right" });
        }
    };

    const updateProductItem = (index: number, field: keyof RequisitionItem, value: any) => {
        const updatedItems = [...requisitionItems];
        
        if (field === "productId" && productOptions) {
            // Find the product name from options
            const selectedProduct = productOptions.find(p => p.value === value);
            updatedItems[index] = {
                ...updatedItems[index],
                [field]: value,
                productName: selectedProduct?.label || ""
            };
        } else {
            updatedItems[index] = {
                ...updatedItems[index],
                [field]: value
            };
        }
        
        setRequisitionItems(updatedItems);
    };
    

    const renderStepContent = () => {
        switch (currentStep) {
            case "requisitionInfo":
                return (
                    <RequisitionInformation
                        control={control}
                        clearErrors={clearErrors}
                        errors={errors}
                        requisition={requisition}
                    />
                );
            case "products":
                return (
                    <div className="space-y-4">
                        {requisitionItems.map((item, index) => (
                            <div key={index} className=" p-4 relative">
                                <div className="flex justify-between items-center gap-4">
                                    <div className="w-3/5">
                                        <FIlterSelect
                                            name={`product-${index}`}
                                            options={productOptions}
                                            className="w-full"
                                            label="Item Name"
                                            value={item.productId}
                                            onChange={(value) => updateProductItem(index, "productId", value)}
                                        />
                                    </div>
                                    <div className="w-2/5">
                                        <Input
                                            value={item.quantity.toString()}
                                            className="w-full"
                                            label="Quantity"
                                            placeholder="Enter Quantity"
                                            type="number"
                                            min="1"
                                            onChange={(e) => updateProductItem(index, "quantity", parseInt(e.target.value) || 0)}
                                        />
                                    </div>
                                    <div
                                        
                                        className="text-red-500 hover:text-red-700"
                                        onClick={() => removeProduct(index)}
                                    >
                                        <Trash2 size={18} />
                                    </div>
                                </div>
                            </div>
                        ))}

                        <div
                           
                            className="flex items-center justify-center gap-2 text-green-600 font-medium mt-4 hover:text-green-800 bg-[#E8F2E8] p-3 rounded-full"
                            onClick={addProduct}
                        >
                            <Plus size={16} />
                            Add New Item
                        </div>
                    </div>
                );
        }
    };

    const renderFooterButtons = () => {
        if(currentStep === "requisitionInfo") {
            return (
                <div className="flex gap-3 py-2 mt-4">
                    <Button
                        type="button"
                        className="hover:bg-green-700 flex items-center"
                        onClick={validateAndProceed}
                        variant="primary"
                        size="lg"
                    >
                        <Icon name="noteIcon" />
                        Proceed to Eligibility & Conditions
                    </Button>
                </div>
            )
        } else if (currentStep === "products") {
            return (
                <div className="flex justify-between gap-2 py-2 mt-4">
                  <button
                    type="button"
                    className="bg-[#E8F2E8] w-full rounded-full hover:bg-gray-200 pl-10 py-2 text-[#047401] flex font-medium"
                    onClick={goBackToPreviousStep}
                  >
                    <Icon name="backIcon" />
                    Requisition Information
                  </button>
                  <Button
                    type="button"
                    variant="primary"
                    size="lg"
                    className="hover:bg-green-700 text-white flex items-center"
                    onClick={validateAndProceed}
                  >
                    {loading ? (
                      <RingLoader color="#ffffff" loading={loading} size={20} />
                    ) : (
                      <div className="flex gap-3">
                        Submit for Approval <Icon name="noteIcon" />
                      </div>
                    )}
                  </Button>
                </div>
              );
        }
        return null;
    };

    return (
        <Modal
            visible={isVisible}
            onCancel={onClose}
             width="80%"
                centered={true}
                footer={null}
                style={{
                padding: 0,
                margin: 0,
                maxWidth: "1200px",
                height: "auto",
                }}
        >
            <h1 className="text-2xl font-bold -mt-2 mb-4">New Purchase Requisition</h1>
            <StepIndicator
                currentStep={currentStep}
                setCurrentStep={setCurrentStep}
            />
            <form onSubmit={handleCreateRequisition}>
                <div className="grid gap-4">{renderStepContent()}</div>
                {renderFooterButtons()}
            </form>
        </Modal>
    );
};

export default AddRequisitionDialog;